import { AlertTriangle } from "lucide-react";
import { MAX_WRITER_SOURCES } from "@/lib/sourceLimit";

/**
 * Warns the editor when not all source material reached the writer.
 *
 * Reads `article.source_report` (backend/source_clip.py →
 * build_source_report). Renders nothing when every source went in full,
 * or on articles written before the report existed (2026-09-23).
 *
 * Statuses: "full" | "trimmed" | "trimmed_at_fetch" | "not_sent".
 */
const fmt = (n) => Number(n || 0).toLocaleString();

function describe(r) {
  if (r.status === "not_sent") {
    return `not sent — the writer only receives the first ${MAX_WRITER_SOURCES} sources`;
  }
  if (r.status === "trimmed_at_fetch") {
    return `page was longer than ${fmt(r.limit)} characters — only the first ${fmt(r.chars_sent)} were fetched`;
  }
  return `${fmt(r.chars_sent)} of ${fmt(r.chars_received)} characters sent`;
}

export default function SourceTrimNotice({ report }) {
  const lost = (report || []).filter((r) => r && r.status !== "full");
  if (lost.length === 0) return null;

  return (
    <div
      data-testid="source-trim-notice"
      className="mb-8 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="font-medium">
            Not all of your source material reached the writer
          </div>
          <ul className="mt-1.5 space-y-1 text-xs text-amber-800">
            {lost.map((r, i) => (
              <li key={i} data-testid={`source-trim-row-${i}`} className="break-words">
                <span className="font-medium">{r.label}</span>
                {" — "}
                {describe(r)}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-xs text-amber-800">
            Check the article covers everything you need. To include the
            rest, paste the missing part into Notes and write it again.
          </div>
        </div>
      </div>
    </div>
  );
}